/** @module csi/view/_QueryMixin */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/comm",
	"csi/constants",
	"csi/def",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/store/PageSize",
	"csi/store/PageSizeWithoutAll",
	"csi/utility/dateUtility",
	"csi/widget/Automation",
	"csi/xml",
	"./_query/Level",
	"./_query/LevelFilter",
	"./_query/Result"
], function(declare, Memory, _, csi, comm, constants, def, csiHeading, html, locale, ExtendedPath, Key, SpecBuilder, sessionConstants, state,
	{localStore, routerStore, sessionStore}, PageSize, PageSizeWithoutAll, dateUtility, Automation, xml, QueryLevel, QueryLevelFilter, QueryResult) {

	/**
	 * TODO: Move anything related to csi/view/Widget to _CustomViewQueryMixin. This should only support basics for Image Browser use of _QueryMixin;
	 * either that or separate out functionality needed for Image Browser into new module? Need to analyze what that would take.
	 */

	var ACTION_URL_SUFFIX = "Action";
	var ROOT_RESULT = "__Root__";

	return declare(null, {
		// Boolean: indicates main store should be reset when stores are reset (assumed true if undefined)
		// FIX: need to investigate if can set this automatically if store is initially set on class
		//bResetStore: true,

		// Boolean: indicates if view can have filterSettings, groupSettings, sortSettings, or reportSettings
		//bSupportsQuerySettings: false,

		// Boolean: indicates if view can optimize handling of non-direct, non-branching paths for expanding to see children
		//bSupportsNonBranchingPathsForExpansion: false,

		/** @property {boolean} dataStoreDriven - set to true if view is driven by data store query */
		//dataStoreDriven: false,

		/** @property {IDataStoreInfo} [dataStoreInfo] - meta-data from data store about capabilities of view */
		//dataStoreInfo: null,

		// String: resource to use when view has no results
		emptyMessageResource: "",

		/** @property {boolean} filterRemember - set to true to store ad-hoc filters in local storage */
		filterRemember: false,

		// Object: custom view query settings for all stores in view
		//filterSettings: null,

		// String[]: list of attribute ids to test when finding by arbitrary text - use in conjunction with store set with bSimpleQueryEngine as true
		//findAttributes: null,

		// Object: custom view group settings for root query level
		//groupSettings: null,

		/** @property {string} _groupUniqueId - internal id used to help identify groups after refreshes */
		//groupUniqueId: "",

		// Boolean: indicates if there are actions that apply to query results
		//hasQueryResultActions: false,

		/** @property {number} incrementalQuerySize - number of results to query incrementally to eventually meet the querySize */
		//incrementalQuerySize: 0,

		// Object: ad-hoc query mapping spec key to array of query values
		// 	- if any value has "*", then use match operator
		//	- if any value is xml.VALUE_BLANK, then use appropriate blanks operator
		//	- otherwise use equals operator
		//query: null,

		// Number: number of results displayed in view
		//queryDisplayCount: null,

		/** @property {number} queryDisplayCountTarget - target number of results to display in view based on querySize and number of times More pressed;
		 * this can be greater than the actual number of query results displayed (queryDisplayCount) due to incremental loading */
		//queryDisplayCountTarget: null,

		/** 
		 * @property {Object} queryForServer - query to be used to formulate server query which includes any merged values due to number formatting; 
		 * do not set directly, but rather set query, or use copyQuery or setQueryValue APIs
		 */
		//queryForServer: null,

		// Number: limit on number of results - defaults to sessionConstants.maxResultSize, set to 0 to remove any limit
		//queryLimit: null,

		/** @property {object} queryOriginal - view's original query (before we load saved ad-hoc filters) */
		//queryOriginal: null,

		// Number: number of results to query at once - 0 means query for all
		//querySize: 0,

		/** @property {boolean} querySizeDisabled - if true, then query size cannot be changed currently, which is different than querySizeFixed which if set should not be changed */
		//querySizeDisabled: false,

		// Boolean: if true then querySize setting cannot be changed through UI
		//querySizeFixed: false,

		// Memory: store of available query sizes
		querySizeStore: PageSize,

		// Memory: store of available query sizes in custom view dialog
		querySizeStoreForCustomView: PageSizeWithoutAll,

		// Memory: store of available specs to filter by containing items with id, name, and spec properties (required if using "query")
		// TODO - move to QueryLevel?
		//querySpecStore: null,

		// Number: total number of results available in store
		//queryTotalCount: null,

		// Object: static options for Values stores
		queryValueStoreOptions: {
			bBlankFilters: true,
			bBlanks: true,
			bCounts: true,
			bEmptyItem: true,
			bIgnoreQueryBySpec: true,
			bPrefill: true
		},

		/** @property {Record<string,string>[]} - quickSearchQuery */
		// quickSearchQuery: null,

		// Object[]: custom view report settings
		//reportSettings: null,

		// Object: custom view sort settings
		//sortSettings: null,

		// Boolean: indicates if view is set to use suspend mode
		suspended: false,

		/** @property {boolean} suspendedRequireFilter - set to true if view is suspended and requires at least one attribute filter before unsuspending and showing results */
		suspendedRequireFilter: false,

		/** @property {boolean} suspendedUntilScrolledIntoView - set to true if view is suspended as part of composite view until scrolled into view */
		suspendedUntilScrolledIntoView: false,

		// Boolean: by default, suspended option is only allowed if there is a toolbar plugin to allow unsuspending view
		suspendIsOption: false,

		// Store: data store to which query is applied (required)
		//store: null,

		// QueryLevel[]: contains meta-data about available data in view
		//_queryLevels: null,

		// Object: object mapping store results to QueryResult that contains view-specific meta-data for each result
		//_queryResultMap: null,

		// Object: object mapping spec key to last Values store queried for key
		//_queryValueStores: null,

		constructor: function() {
			this.query = this.queryForServer = {};
			this.groupUniqueId = _.uniqueId("Group");
			this._queryResultMap = {};
			this._queryValueStores = {};
		},

		canFilter: function() {
			// summary: by default, filtering is enabled
			return this.bSupportsQuerySettings && (this.dataStoreInfo?.canFilter ?? true) && this.hasPlugin("filter");
		},

		canGroup: function() {
			// summary: by default, cannot allow grouping if filtering is not enabled, and also rule out when a switchboard
			return this.canFilter() && (this.dataStoreInfo?.canGroup ?? true) && !this.defView.bSelectFirst;
		},

		canReport: function() {
			// summary: return true if view is able to report on results
			return this.bSupportsQuerySettings && this.relRoot && !this.relRoot.reportType;
		},

		canSort: function() {
			// summary: return true if view is able to sort results
			return this.bSupportsQuerySettings && (this.dataStoreInfo?.canSort ?? true);
		},

		/**
		 * Copy query from one query mixin to another
		 * @param {_QueryMixin} qm
		 */
		copyQuery: function(qm) {
			this._set("queryForServer", qm.queryForServer);
			this._set("query", qm.query);
		},

		doesQueryTotalExceedMax: function() {
			// summary: determine if number of results in view exceeds limit allowed for user;
			// if no total, then results have not yet been queried and assume the worst (return true)
			const max = sessionConstants.siteMaxExportSize;
			const total = this.queryTotalCount;
			return (total == null) || (max && (total > max));
		},

		/**
		 * Return true if there are results for widget
		 * @return {boolean}
		 */
		hasQueryResults: function() {
			return !!this.queryTotalCount;
		},

		getConfigurableQueryLevelStore: function() {
			// summary: return store of query levels whose filters are configurable
			if (!this._queryLevels) {
				// this should only be necessary for custom views after view has already loaded and queryLevels exist
				console.assert(false);
				return null;
			}

			var items = _.filter(this._queryLevels, function(ql) {
				return ql.bConfigurable;
			});

			return new Memory({data: items});
		},

		/** 
		 * @override
		 * Return configurable query levels as an array 
		*/
		getConfigurableQueryLevels() {
			if (!this._queryLevels) {
				// this should only be necessary for custom views after view has already loaded and queryLevels exist
				console.assert(false);
				return null;
			}

			return this._queryLevels?.filter((ql) => ql.bConfigurable);
		},

		getMainQueryLevel: function() {
			// summary: return main query level - make sure query levels are created first
			this._createQueryLevels();
			return _.findWhere(this._queryLevels, {bMain: true});
		},

		/**
		 * Return list of visible query results at main level in order as shown in view
		 * @return {QueryResult[]}
		 */
		getMainQueryResults: function() {
			var qrRoot = this.getQueryResult();
			var results = [];

			function traverse(qr) {
				if (qr.bHidden) {
					return;
				}

				var bGroup = qr.isGroup();
				if (bGroup) {
					_.each(qr.childResults, traverse);
				} else if (qr.bResultNode) {
					results.push(qr);
				}
			}

			// traverse children under root result
			_.each(qrRoot.childResults, traverse);

			return results;
		},

		/**
		 * Return list of result urls in view
		 * @return {cnl[]}
		 */
		getMainQueryResultUrls() {
			const qrRoot = this.getQueryResult();
			return qrRoot.getChildResultUrls(true);
		},

		getQueryLevel: function(/*String*/qlid) {
			// summary: return query level by id
			return _.findWhere(this._queryLevels, {id: qlid});
		},

		/**
		 * Return dependency paths required for level based on view settings (sort, filter, etc.)
		 * @param {QueryLevel} ql
		 * @return {string[]}
		 */
		getQueryLevelDependencyPaths: function(ql) {
			var paths = [];

			// add paths from custom view filter settings
			var qlf = this.getQueryLevelFilter(ql);
			if (qlf) {
				qlf.addPaths(paths);
			}

			// add paths from custom view sort settings
			_.each(this.getQueryLevelSortOptions(ql), function(sort) {
				var path = xml.splitQueryKey(sort.attribute).path;
				if (path) {
					paths.push(path);
				}
			});

			// if performing reverse aggregation involving context node, then need path to query for them
			// FIX: move to aggregate action controller
			var fd = this.defView.m_formData;
			if (fd && ql.bCanAggregate) {
				var action = fd.getAction();
				if (action.bReverse && action.aggregateClass) {
					paths.push(xml.path(action.aggregateId));
				}
			}

			return paths;
		},

		getQueryLevelFilter: function(/*QueryLevel*/ql) {
			// summary: return query filter settings for given level
			if (!ql) {
				return null;
			}

			var obj = this.get("filterSettings");
			return new QueryLevelFilter(this, obj ? obj[ql.id] : null, ql);
		},

		/**
		 * Return sort options for sort settings on given query level
		 * @param {QueryLevel} ql
		 * @return {Object}
		 */
		getQueryLevelSortOptions(ql) {
			const {bMain, bOrdered} = ql;

			// if not the main level, and this level is ordered, then view sort settings should not override nested order
			if (bOrdered && !bMain) {
				return null;
			}

			return csi.transform(this.get("sortSettings"), (sortObj) => {
				const spec = this.getHeadingSpec(sortObj.key);
				return spec?.queryKey ? {attribute: spec.queryKey, descending: sortObj.descending} : null;
			});
		},

		getQueryLimitIfReached: function(/*QueryResult?*/qrParent) {
			// summary: return limit if queryTotal exceeds it
			if (qrParent = qrParent || this.getQueryResult(null, true)) {
				var limit = qrParent.getChildCountLimit();
				var total = qrParent.isRoot() ? this.queryTotalCount : qrParent.getChildTotalItemCount();
				return (total > limit) ? limit : 0;
			}

			return 0;
		},

		/**
		 * Return context to obtain relative query values if relative filtering is supported in view
		 * @return {Context | undefined}
		 */
		getQueryRelativeContext() {
			const {relativeQueryContextUrl, urlRoot} = this;
			let ctx;

			if (relativeQueryContextUrl) {
				ctx = this.getContext(relativeQueryContextUrl);
			} else {
				ctx = this.getSourceContext() || this.getContext(urlRoot);
			}

			const rc = ctx.getNodeClass();

			if (rc && !rc.isSingleton()) {
				return ctx;
			}
		},

		/**
		 * Return relationships that should map to query levels
		 * @return {Relationship[]}
		 */
		getQueryRelationships() {
			return this.defView.getRelationships();
		},

		getQueryResult: function(/*String?*/id, /*Boolean?*/bExists) {
			// summary: return QueryResult given id (if no id given, then use root id)
			var qr = this._queryResultMap[id || ROOT_RESULT];

			// ensure that query levels are created first if getting root query result whether it already exists or not
			if (!id && !bExists) {
				this._createQueryLevels();
			}

			// if it exists, or only asking for those that exist, then return now (whether it exists or not)
			if (qr || bExists || id) {
				return qr;
			}

			// only create root query result lazily (otherwise use _createQueryResult directly)
			var ql = this._queryLevels && this._queryLevels[0];
			if (ql) {
				return this._createQueryResult({
					bResultNode: !!ql.bResults,
					id: ROOT_RESULT,
					level: ql,
					store: (ql.childLevel && ql.childLevel.bMain) ? this.store : null,
					url: (this.relRoot && this.relRoot.m_url) || this.urlRoot
				});
			}

			return null;
		},

		getQueryResultMessage: function(/*Number?*/nStart, /*Number?*/nEnd, /*Number?*/total) {
			// summary: return message indicating number of top-level results shown in view
			total = total ? total : this.queryTotalCount;
			if (total == null) {
				return "";
			}

			// use specific start and end message when printing views spanning multiple pages
			if (nStart && (nEnd || (nStart > 1))) {
				return locale.resource("SEARCH_DISPLAY_RESULTS", [nStart, nEnd || total, total]);
			}

			var displayTarget = this.queryDisplayCountTarget;
			var displayed = this.queryDisplayCount;
			var bGroups = this.hasGroups();
			var store = this.get("store");
			var totalString = total;
			var pluralResults = total > 1;
			var pluralGroups;
			var res;

			// the actual display count could be lower than the target due to incremental loading, but we want to display the target count
			if (displayTarget) {
				console.assert(displayTarget >= displayed);
				displayed = Math.min(displayTarget, total);
			}

			if (store && store.bQueryCountLimited && total) {
				totalString = locale.resource("QueryLimitCount", [total]);
			}

			var params = {
				displayCount: displayed,
				resultCount: totalString
			};

			// validate that groups are actually displayed - if the view had no results, then groups may not be displayed
			if (bGroups) {
				var ql1 = this._queryLevels[1];
				if (ql1 && !ql1.groupSpec) {
					bGroups = false;
				}
			}

			if (!total) {
				// no groups or results in view
				res = this.emptyMessageResource;
			} else if (bGroups) {
				// if grouping, then display group count as well as result count
				var qr = this.getQueryResult();
				var nGroupsDisplayed = qr.getChildDisplayCount();
				var nGroupsTotal = qr.getChildTotalCount();

				pluralGroups = nGroupsTotal > 1;
				params.groupCount = nGroupsTotal;
				params.groupDisplayCount = nGroupsDisplayed;

				if (!nGroupsDisplayed) {
					// no groups displayed, so must be showing count in suspend mode
					res = "SEARCH_COUNT";
				} else if (nGroupsDisplayed < nGroupsTotal) {
					// showing partial groups
					if (!displayed) {
						// no groups expanded
						res = "SEARCH_SOME_GROUPS_NONE_EXPANDED";
						params.displayCount = qr.getChildDisplayedItemCount();
					} else {
						// some expanded
						res = "SEARCH_SOME_GROUPS_SOME_EXPANDED";
					}
				} else {
					// showing all groups
					if (!displayed) {
						// no groups expanded
						res = "SEARCH_ALL_GROUPS_NONE_EXPANDED";
					} else if (displayed < total) {
						// some expanded
						res = "SEARCH_ALL_GROUPS_SOME_EXPANDED";
					} else {
						// all expanded
						res = "SEARCH_ALL_GROUPS_ALL_EXPANDED";
					}
				}
			} else if (!displayed || this.get("suspended")) {
				// showing count in suspend mode
				res = "SEARCH_COUNT";
			} else if ((displayed < total) && (total != Infinity)) {
				// showing partial results
				res = "SEARCH_RESULTS_OF_TOTAL";
			} else {
				// showing all results
				res = "SEARCH_RESULTS";
			}

			// ready to compose message
			var message = locale.getResultCountString(res, params, pluralResults, pluralGroups);

			// test total against max result size limit to see if view has reached limit
			if (total != Infinity) {
				var limit = this.getQueryLimitIfReached();
				if (limit) {
					message += locale.resource("SEARCH_EXCEEDS_LIMIT", [limit]);
				}
			}

			return message;
		},

		getQueryResultOptions: function(/*QueryResult?*/qr, /*Boolean*/more) {
			// summary: return query options object for given result
			qr = qr || this.getQueryResult();

			let {incrementalQuerySize, queryDisplayCountTarget, querySize} = this;
			const {childLevel} = qr.level;
			const currentCount = qr.getChildDisplayCount();
			const queryOptions = {};

			if (incrementalQuerySize && (!querySize || (querySize > incrementalQuerySize))) {
				// if loading incrementally, then determine how much (more) needs to be loaded;
				// only need to increase size of current display count when user increases the results size - in such a case,
				// then increase the current count by the incremental query size; otherwise, just load the next
				// increment of results
				if (!more && (queryDisplayCountTarget < querySize)) {
					// make sure we handle going from a results size of 20 to 50
					const q = Math.ceil(currentCount / incrementalQuerySize);

					querySize = q * incrementalQuerySize;

					if ((querySize - currentCount) < incrementalQuerySize) {
						querySize += incrementalQuerySize;
					}
				} else {
					querySize = incrementalQuerySize;
				}
			}

			if (querySize || more) {
				queryOptions.start = more ? currentCount : 0;
				queryOptions.count = querySize;
			}

			// add sort options that apply to this level
			if (childLevel) {
				const sorts = this.getQueryLevelSortOptions(childLevel);
				if (sorts) {
					queryOptions.sort = sorts;
				}

				// if initial query for main results, then apply query count limit
				if (childLevel.bMain) {
					queryOptions.requestParameters = {
						"Qry.Limit.Filter": constants.QUERY_COUNT_LIMIT
					};
				}

				// add quick search query which applies to main level and any group levels
				if (childLevel.bMain || childLevel.groupSpec) {
					queryOptions.quickSearchQuery = this.quickSearchQuery;
				}
			}

			return queryOptions;
		},

		getQueryResultQuery: function(/*QueryResult?*/qr) {
			// summary: only apply query if root level - otherwise apply client ad-hoc query if one
			qr = qr || this.getQueryResult();
			return qr.getQuery() || {};
		},

		getQueryResults: function() {
			// summary: return all QueryResults
			return _.values(this._queryResultMap);
		},

		getQueryValue: function(/*String*/id) {
			// summary: return value(s) for id if they exist
			return this.query[id];
		},

		getQueryValueAsLabel: function(/*String*/id, sort = true) {
			// summary: return text to display for selected value
			var spec = this.getSpec(id);
			var store = this._queryValueStores[id];
			var value = this.getQueryValue(id);

			// treat date filter values differently as it is a single-value rather than multi-value filter
			if (value && spec && spec.isDateAttribute()) {
				return [this.getSpecValueLabel(id, value)];
			}

			var labels = _.map(value, function(v) {
				if (xml.queryIsMatchValue(v)) {
					return xml.queryFromMatchValue(v);
				}

				// try to get item from store if it is already in memory, otherwise, generate text from value
				var valueItem = store && store.getFromMemory(v);
				const val = valueItem ? valueItem[store.labelProperty] : this.getSpecValueLabel(id, v);
				// if val is still undefined or "" return localized "(blanks)"
				return val || locale.resource(xml.VALUE_BLANK);
			}, this);

			if (!sort) {
				return labels;
			}

			return csi.sort(labels);
		},

		/**
		 * @param {string} id 
		 * @param {boolean} [ignoreSelectedValues] 
		 */
		getQueryValueStore(id, ignoreSelectedValues) {
			// summary: return store for item with given id
			var store = this._queryValueStores[id];
			if (!store) {
				store = this._queryValueStores[id] = this.getSpecValues(id, _.clone(this.queryValueStoreOptions));

				if (!store) {
					return null;
				}
			}

			// make sure existing filters are in store
			if (store.removeAllSelectedValues) {
				store.removeAllSelectedValues();
			}

			// add selected values into store in case they are different than existing data, except for date attributes;
			// date attributes specify a date range whose values do not need to be in the store
			var spec = this.getSpec(id);

			if (!spec?.isDateAttribute() && !ignoreSelectedValues) {
				_.each(this.getQueryValue(id), function(v) {
					if (xml.queryIsMatchValue(v)) {
						store.addSelectedValue(v, xml.queryFromMatchValue(v), true);
					} else {
						store.addSelectedValue(v, this.getSpecValueLabel(id, v));
					}
				}, this);
			}

			return store;
		},

		/**
		 * Return names for relationships in the view
		 * @param {Relationship[]} relationships
		 * @return {string[]}
		 */
		getRelationshipNames(relationships) {
			const types = {};

			return relationships.map((rel) => {
				const type = rel.getTargetType();
				const name = locale.type(type);
				let secondaryName = this.onQueryRelationshipName(rel);

				if (!secondaryName) {
					if (types[type]) {
						secondaryName = String(++types[type]);
					} else if (!secondaryName) {
						types[type] = 1;
					}
				}

				return secondaryName ? locale.resource("QueryLabelWithClass", [name, secondaryName]) : name;
			});
		},

		/**
		 * Return result node type
		 * @return {string}
		 */
		getResultNodeType: function() {
			const resultClass = this.defView.getPageClass();
			return resultClass ? resultClass.getType() : null;
		},

		getSpec: function(/*String*/id) {
			// summary: override to return csi/heading/Spec for given id
			var item = this.querySpecStore && this.querySpecStore.get(id);
			return item ? item.spec : null;
		},

		getSpecValues: function(/*String*/id, /*Object*/storeOptions) {
			// summary: override to return csi/store/Values for Spec with given id
			var store = this.get("store");
			if (store && store.getValuesStore) {
				var spec = this.getSpec(id);
				if (spec && spec.querySpec) {
					return store.getValuesStore(spec, storeOptions);
				}
			}

			return null;
		},

		getSpecValueLabel: function(/*String*/id, /*Any*/value) {
			// summary: return level for given value of spec
			return csiHeading.getTextForFilterValue(this.getContext(), this.getSpec(id), value);
		},

		getSpecValuesWidgetParams: function(/*String*/id) {
			// summary: return set of parameters to use with multi-select widget
			var spec = this.getSpec(id);
			if (!spec) {
				return {};
			}

			var value = this.getQueryValue(id);
			var valuesStore = this.getQueryValueStore(id);

			if (!valuesStore) {
				return null;
			}

			if (spec.isDateAttribute()) {
				// date range widget does not need (All) or blanks items in store
				valuesStore.bBlankFilters = false;
				valuesStore.bBlanks = false;
				valuesStore.bEmptyItem = false;

				// time filtering uses different widget with different parameters than multi-select
				// FIX: should probably change multi-select to work this way too, but for now just hard-code for time attributes
				var ren = csiHeading.getHeadingRenderer(null, spec.heading);
				var params = ren.getDateRangeParams(spec);
				params.value = value || [];

				if (dateUtility.isDateOnlyFormat(spec.format)) {
					params.query = this.query;
					params.store = valuesStore;
				}

				return params;
			}

			return Automation.params(this.defView && this.defView.getHtmlKey(), "values", id, {
				addNewItem: spec.canFilterByOperator(xml.OP_MATCHES),
				classAttr: valuesStore.cssProperty,
				defaultClearText: "SelectAll",
				labelAttr: valuesStore.bCounts ? valuesStore.labelWithCountProperty : valuesStore.labelProperty,
				pageSize: 100,
				placeHolderLabelAttr: valuesStore.labelProperty,
				query: this.queryForServer,
				searchAttr: valuesStore.labelProperty,
				showMatchEntry: true,
				store: valuesStore,
				value: value
			});
		},

		/**
		 * Return true if there is no ad-hoc query in the view
		 */
		hasEmptyQuery() {
			return _.isEmpty(this.get("query")) && !this.quickSearchQuery;
		},

		/**
		 * Return true if there are groups in the view
		 * @return {boolean}
		 */
		hasGroups: function() {
			const {groupSettings} = this;
			return groupSettings?.key1 || groupSettings?.key2;
		},

		hasQueryValue: function(/*String*/id) {
			// summary: return true if filter value for given id
			var value = this.getQueryValue(id);
			return value && (value.length > 0);
		},

		hasSingleQueryValue: function(/*String*/id, /*Boolean*/bMatch) {
			// summary: return true if single filter value for given id, and optionally if it uses match operator
			var value = this.getQueryValue(id);
			if (value && (value.length == 1)) {
				return !bMatch || xml.queryIsMatchValue(value[0]);
			}

			return false;
		},

		isSuspended: function() {
			// summary: view can only be suspended if it is set as such and the option is available
			return this.suspended && (this.suspendIsOption || this.suspendedUntilScrolledIntoView);
		},

		onChangeData: function() {
			// summary: extension point to react to change in any data in view
			this._queryValueStores = {};
			this.inherited(arguments);
		},

		/**
		 * set the group settings for the default custom view
		 * @param {string} id 
		 * @param {string} path
		 */
		setDefaultGroupAndExpand(id, path = null, bExpand = true) {
			const {_defaultCustomView} = this;
			const extendedPath = ExtendedPath.create(_defaultCustomView.targetClass, path);
			const typedKey = new Key({extendedPath, id}).typedString;
			_defaultCustomView.setValue("groupSettings", {key1: typedKey, expand1: bExpand});
		},

		/**
		 * Set query value(s) for Spec with given id
		 * @param {string} id - spec id
		 * @param {Any} value - query value(s)
		 * @return {boolean} returns true if query changed
		 */
		setQueryValue: function(id, value) {
			// summary: set value for filters for store item with given id
			var oldValue = this.getQueryValue(id) || [];
			var newValue = value;

			if (value == null) {
				newValue = [];
			} else if (!_.isArray(value)) {
				newValue = [value];
			}

			// VALUE_NONBLANK will only be in store with VALUE_BLANK, so if both exist, then do nothing
			if ((newValue.length == 2) && (_.indexOf(newValue, xml.VALUE_NONBLANK) >= 0)) {
				newValue = [];
			}

			// date filter values should not be sorted
			var spec = this.getSpec(id);
			var valuesStore = spec.isDateAttribute() ? null : this._queryValueStores[id];
			var mergedValues;

			if (valuesStore) {
				newValue = _.map(newValue, function(v) {
					var valueItem = valuesStore.getFromMemory(v);
					if (valueItem) {
						// use getIdentity rather than value to make sure value is number for numeric attributes types -
						// multiSelect widget always returns strings
						v = valuesStore.getIdentity(valueItem);

						// a single query value can sometimes represent multiple actual values (i.e. due to number formatting),
						// so in this case need to track both so that when query is sent to the server, then all values can be queried against
						var itemMerged = valueItem[valuesStore.mergedValuesProperty];
						if (itemMerged) {
							mergedValues = mergedValues || [];
							mergedValues = mergedValues.concat(itemMerged);
						}
					}

					return v;
				});

				oldValue.sort();
				newValue.sort();
			}

			// if no change then stop here
			if (_.isEqual(oldValue, newValue)) {
				return false;
			}

			// assign new filters
			var q;
			var qs;

			if (newValue.length) {
				// update "query"
				q = _.clone(this.query);
				q[id] = newValue;

				// update "queryForServer" - if no merged values have been introduced, then it should be kept equal to "query"
				if (mergedValues || (this.query != this.queryForServer)) {
					qs = _.clone(this.queryForServer);
					qs[id] = newValue;

					if (mergedValues) {
						qs[id] = qs[id].concat(mergedValues);
					}
				} else {
					qs = q;
				}
			} else {
				// update "query"
				q = _.omit(this.query, id);

				// update "queryForServer"
				if (this.query != this.queryForServer) {
					qs = _.omit(this.queryForServer, id);
				} else {
					qs = q;
				}
			}

			// set queryForServer first as most listeners will be watching "query", and we want "queryForServer" to already be set when that happens
			this._set("queryForServer", qs);
			this._set("query", q);

			// remove old match filters from store since they are no longer selected
			if (valuesStore) {
				_.each(_.difference(oldValue, newValue), function(v) {
					valuesStore.removeSelectedValue(v);
				});
			}

			// save ad-hoc filter changes to local storage
			if (this.filterRemember) {
				this.saveAdHocFilters();
			}

			return true;
		},

		_createMainStore: function() {
			// summary: create data store for main level if does not already exist; the first query level may not be the main one due to grouping -
			// if it is, then reuse store from root query result; otherwise create store from main query level given current context
			var qr = this.getQueryResult();
			if (qr) {
				var qlMain = this.getMainQueryLevel();
				var store = (qlMain == qr.level.childLevel) ? qr.getStore() : qlMain.createStore(qr.getContext());
				this._set("store", store);
			}
		},

		_createQueryLevel: function(/*Object*/params, /*Boolean*/bRoot) {
			// summary: return new QueryLevel with given params
			return new QueryLevel(params);
		},

		loadAdHocFilters: function() {
			// load saved adhoc filter, remember predefined view's query
			if (!sessionStore.print) {
				// set queryOriginal. Set it only once only on first call to loadAdHocFilter
				if (this.queryOriginal === undefined) {
					this.queryOriginal = {...this.query};
				}
				const context = this.get("customViewContext");
				const adhocs = localStore.getAdhocFilters(context);
				if (adhocs) {
					const mergedAdHocs = {
						...this.queryOriginal,
						...adhocs
					}
					this.set("query", mergedAdHocs);
				}
			}
		},

		saveAdHocFilters: function() {
			// Save user's ad-hoc changes 
			const context = this.get("customViewContext");
			// do not save view's predefined ad-hocs, only save user changes
			const adhocs = {...this.queryForServer};
			this.queryOriginal && Object.keys(this.queryOriginal).forEach(key => {
				if (adhocs[key]) {
					delete adhocs[key];
				}
			})
			localStore.setAdhocFilters(context, adhocs);
		},

		_createQueryLevels: function() {
			// summary: create level for each relationship in view that supports filtering which basically is any
			// except for matrices where filtering is not allowed; return true if levels were generated
			if (this._queryLevels) {
				return;
			}

			const {defView, urlRoot} = this;
			const ctx = this.getContext(urlRoot);
			const levels = this._queryLevels = [];

			if (defView) {
				// add query level for root url of view - whether or not it is actually displayed; it will be used to query for results in view
				// and not for configuration of filters, so it does not need a name or configurableStore
				const specBuilder = new SpecBuilder(defView.getRootClass(), ctx, defView.pathHintsContext);
				let ql = this._createQueryLevel({
					id: QueryLevel.ROOT_LEVEL,
					qm: this,
					specBuilder,
					type: this.getViewNodeType()
				}, true);

				console.assert(!!ql, "Root query level must exist!");
				levels.push(ql);

				const bConfigurable = this.supportsCustomViews();
				const bDormant = routerStore.locationIsDormant;

				// create level for each Relationship, including those used for column matrices that allow filtering
				const rootNode = ctx.getNode();
				const relationships = this.getQueryRelationships();
				const relationshipNames = this.getRelationshipNames(relationships);

				relationships.forEach((r, i) => {
					// determine unique name for QueryLevel
					const type = r.getTargetType();
					const name = relationshipNames[i];
					const params = {
						bConfigurable,
						id: String(r.key),
						name,
						pathHints: r.pathHintsContext,
						qm: this,
						type,
						_rel: r
					};

					const bMain = !i;
					const specBuilder = bMain ? this.specBuilder : this.createSpecBuilder(r);

					// add parameters necessary to support QueryResult which are not used with column relationships
					_.extend(params, {
						actions: bDormant ? null : r.m_defActions,
						bMain,
						bOrdered: r.bOrdered,
						bReadOnly: r.bReadOnly,
						bRelativeQuery: r.isRelative(),
						bResults: true,
						css: r.m_class,
						moveAction: bDormant ? null : r.moveAction,
						newActions: bDormant ? null : r.m_news,
						parentAttribute: r.getAttributeId(),
						requestParameters: {
							"Dep.Path": def.getRelationshipDependencyPaths(r)
						},
						rules: r.m_rules,
						specBuilder,
						typeSortAttribute: r.typeSortAttribute
					});

					// add parameter for optimized handling of table expansion
					var onlyPath = r.getOnlyPath();
					if (onlyPath) {
						// all tables can optimize handling of direct relationship to children, but only some can support paths with more steps
						if (r.m_id || (r.bSimplePath && this.bSupportsNonBranchingPathsForExpansion)) {
							params.resultPath = onlyPath;
						}
					}

					// Add a parameter to request additional _CS_FileInfo nodes if we are showing file sizes
					if (this.showFileSizes) {
						params.requestParameters["Ext.File.Info"] = true;
					}

					// impose limit of number of results in view unless explicitly set to 0
					if (this.queryLimit != 0) {
						params.requestParameters["Fmt.Complete.Max"] = this.queryLimit || sessionConstants.maxResultSize;
					}

					// create QueryLevel
					if (ql = this._createQueryLevel(params)) {
						// handle repeating relationships by self-referencing childLevel
						if (r.m_bRepeat) {
							ql.childLevel = ql;
						}

						// add dependency paths for sort and filter conditions for this query level
						ql.addDependencyPaths(this.getQueryLevelDependencyPaths(ql));

						levels.push(ql);
						this.onCreateQueryLevel(ql);

						if (ql.bMain) {
							this.onCreateMainQueryLevel(ql);
						}

						if (r.dataStore) {
							ql.configureForDataStore(r.dataStore, rootNode?.$URL);
						}

						// add store to be used to generate auto-complete filter values when defining filters, but only needed if relationship can be filtered;
						// do so after QueryLevel is fully created to make sure to capture all store parameters
						if (ql.bConfigurable) {
							if (i && rootNode) {
								// use context if first, but not when nested table relationship;
								// this store is used just to support Values queries when defining filter conditions
								// rather than used to return actual result nodes
								ctx.setNode(null);
							}

							ql.configurableStore = ql.createStore(ctx, true);
						}
					}
				}, this);
			}

			// iterate through query levels to assign childLevel
			_.each(levels, function(ql, i) {
				if (!ql.childLevel) {
					var qlChild = levels[i + 1];

					if (qlChild && qlChild.bResults) {
						ql.childLevel = qlChild;
					}
				}
			});

			// call extension point to post-process levels
			this._onCreateQueryLevels(this._queryLevels);

			// determine if there are actions at any displayed level, check the configuration as well
			const hasActions = ctx.allowActions() && this.bShowActionColumn &&
				!!_.some(levels, (level) => level.bResults && level.actions?.hasActions());

			this._set("hasQueryResultActions", hasActions);

			// iterate through levels and determine if any have dependency paths with Parent steps; if so, then
			// suggest that view should probably use its own cache rather than share
			if (!this.defView.bOwnCache) {
				_.each(this._queryLevels, function(ql) {
					if (_.some(ql.getDependencyPaths(), function(p) {return p.indexOf("Parent:") >= 0;})) {
						console.assert(false, 'Use OwnCache="1" if using path with Parent step.');
					}
				});
			}
		},

		/**
		 * Extension point to allow configuration of the main query level
		 * @param {QueryLevel} ql
		 */
		onCreateMainQueryLevel: function(ql) { },

		/**
		 * Extension point to allow configuration of any query level
		 * @param {QueryLevel} ql
		 */
		onCreateQueryLevel: function(ql) { },

		/**
		 * Extension point to return custom name for relationship to appear in custom view dialog
		 * @param {Relationship} relationship 
		 * @return {string}
		 */
		onQueryRelationshipName(relationship) {
			return "";
		},

		/**
		 * Return new query result with given params
		 * @param {Object} params 
		 * @param {QueryResult} [qrNext] - optional query result to insert new result before
		 * @return {QueryResult}
		 */
		_createQueryResult: function(params, qrNext) {
			const qr = new QueryResult(params);
			const qrOld = this._queryResultMap[qr.id];

			// this could happen for root query result since it is added by default, but should not happen for others
			console.assert(!qrOld || qrOld.isRoot(), "QueryResult should be removed before added again");

			this._queryResultMap[qr.id] = qr;

			// add child to parent result
			const {parentResult} = qr;

			if (parentResult) {
				parentResult.addChild(qr, qrNext);
			}

			return qr;
		},

		/**
		 * Set action context by setting html attributes on plugin's button node
		 * @property {QueryResult} qrParent
		 * @property {Object} ctx
		 * @property {DomNode} domNode
		 * @property {QueryResult} [qr]
		 * @return {QueryResult}
		 */
		setQueryResultActionContext: function(qrParent, ctx, domNode, qr) {
			if (!qr) {
				let {
					hl,
					level,
					url
				} = qrParent;

				// action context for action rows should always be the main query level if given the root result;
				// this applies to actions in the toolbar, placeholder, and main level of the table whether
				// grouped or not
				level = qrParent.isRoot() ? this.getMainQueryLevel() : level.childLevel;

				qr = this._createQueryResult({
					bAction: true,
					hl: hl + 1,
					level,
					parentResult: qrParent,
					parentUrl: url,
					url: xml.getClientNodeUrl(ACTION_URL_SUFFIX),
					urlResultForAction: qrParent.getNextResultUrl()
				});
			}

			this.setRefNodeId(domNode, qr.id);
			this.setHierarchyLevel && this.setHierarchyLevel(domNode, qr.hl);
			html.setDataAttribute(domNode, html.DATA_RESULT_ACTION, true);
			ctx.applyActionContext(domNode, "");

			return qr;
		},

		_getStoreAttr: function() {
			// summary: return data store for main level (first relationship)
			if (!this.store) {
				this._createMainStore();
			}

			return this.store;
		},

		_insertQueryResult: function(/*QueryResult*/qr, /*Boolean*/bPersistent, /*QueryResult*/qrNext) {
			// summary: insert query result into view

			// if change is persistent, then add result to store
			if (bPersistent && !qr.bCompareNode) {
				qr.insertInStore(qrNext);
			}

			// track number of main results
			if (qr && qr.includeInCount()) {
				if (bPersistent) {
					this._set("queryTotalCount", this.queryTotalCount + 1);

					if (this.queryDisplayCountTarget != null) {
						this._set("queryDisplayCountTarget", this.queryDisplayCountTarget + 1);
					}
				}

				this._set("queryDisplayCount", this.queryDisplayCount + 1);
			}
		},

		/**
		 * Query for full count when user clicks on limited count in footer message
		 */
		_onClickQueryMessageCount: function() {
			var self = this;
			var qr = this.getQueryResult();
			var queryOptions = {
				requestParameters: {
					"Operation": qr.getStore().requestOperationCount
				}
			};

			this.when(this._query(qr, queryOptions), function() {
				self.set("message");
			});
		},

		_onCreateQueryLevels: function(/*QueryLevel[]*/queryLevels) {
			// summary: extension point to react to change in query levels
		},

		/**
		 * If relative filters are in use, then make sure to request the node in context, as well as any paths necessary to calculate the relative values
		 * @param {Request} request
		 */
		_onRootRequest: function(/*Request*/request) {
			var ctx = this.getQueryRelativeContext();
			if (ctx) {
				var paths = [];

				_.each(this.get("filterSettings"), function(value, key) {
					new QueryLevelFilter(this, value).addRelativePaths(paths);
				}, this);

				request.addParameter(comm.QUERY_URL, [ctx.getNodeURL()], true);
				request.addDependencyPaths(paths);
			}

			this.inherited(arguments);
		},

		/**
		 * Query store for query result
		 * @param {QueryResult} [qr]
		 * @param {Object} [queryOptions]
		 * @param {Store} [store]
		 * @return {Deferred} 
		 */
		_query: function(qr, queryOptions, store) {
			qr = qr || this.getQueryResult();
			queryOptions = queryOptions || this.getQueryResultOptions(qr);
			var query = this.getQueryResultQuery(qr);
			var dfd = store ? store.query(query, queryOptions) : qr.query(query, queryOptions);
			var self = this;

			return this.when(dfd, function(results) {
				if (qr.isRoot()) {
					var total = store ? results.total : qr.getResultItemCount(results);
					self._setMainQueryLevelTotal(total);
				}

				return results;
			});
		},

		_removeQueryResult: function(/*QueryResult*/qr, /*Boolean?*/bPersistent, /*Boolean?*/bReparenting, /*Boolean?*/bOmitCount) {
			// summary: remove result from map
			if (qr && this._queryResultMap[qr.id]) {
				// if change is persistent, then also remove result from store rather than just view
				if (qr.parentResult) {
					qr.parentResult.removeChild(qr, bPersistent);
				}

				// in cases such as Excel, query results are created, but not inserted; in such cases, need to skip counting of them
				if (!bOmitCount) {
					// track number of main results - do not allow to go below 0 which could happen when reloading view
					// and query results are only removed after count has been reset to 0
					if (qr.includeInCount()) {
						// when expanding a group, a result can be removed from one group and added back to another -
						// in that case the total needs to remain the same
						if (bPersistent && !bReparenting) {
							this._set("queryTotalCount", this.queryTotalCount - 1);

							if (this.queryDisplayCountTarget != null) {
								this._set("queryDisplayCountTarget", Math.max(this.queryDisplayCountTarget - 1, 0));
							}
						}

						if (!qr.bHidden) {
							this._set("queryDisplayCount", Math.max(this.queryDisplayCount - 1, 0));
						}
					} else if (bPersistent && !bReparenting && qr.isGroup() && !qr.getChildDisplayCount()) {
						// if deleting a collapsed group, then update total count
						const count = qr.getGroupCount() ?? 0;
						this._set("queryTotalCount", this.queryTotalCount - count);
					}
				}

				delete this._queryResultMap[qr.id];
			}
		},

		_resetLevels: function() {
			// summary: reset configuration
			this._queryLevels = null;
			this._queryResultMap = {};
			this._set("querySpecStore", null);
		},

		_resetQueryResults: function() {
			// summary: remove all query results except for root
			var qr = this.getQueryResult(null, true);

			this._queryResultMap = {};

			if (qr) {
				qr.resetChildren();
				this._queryResultMap[qr.id] = qr;
			}
		},

		_resetStores: function() {
			// summary: reset data stores
			this._queryValueStores = {};

			if (this.bResetStore !== false) {
				var qr = this.getQueryResult(null, true);
				if (qr) {
					qr.resetStore(true);
				}

				this._set("store", null);
			} else if (this.store && this.store.refreshTimestamp) {
				// if not resetting entire store, then at least reset timestamp so that it queries for new version of data in store
				this.store.refreshTimestamp = this.refreshTimestamp;
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Initialize query settings
		 */
		_resolveView: function() {
			const {dataStoreInfo, defView, relRoot} = this;

			// turn back to suspend mode if there is no filter and 
			// require at least one filter require option is checked in the custom view
			this.watch("query", (attr, oldValue, newValue) => {
				if (this.suspendedRequireFilter && !this.suspended && _.isEmpty(newValue)) {
					this.set("suspended", true);
				}
			});

			this.inherited(arguments);

			this.moreAction = defView._addViewAction("ViewQueryMore", {
				m_class: html.CSS_BUTTON_SECONDARY + " csi-more-btn",
				m_typeControl: def.ACTION_BUTTON
			});
			this.showAllAction = defView._addViewAction("ViewQueryAll", {});

			if (this.querySizeFixed === undefined) {
				this.querySizeFixed =
					state.get("print") ||
					!(dataStoreInfo?.canPage ?? true) ||
					defView.m_bSlim ||
					(defView.m_nPageSize === 0) ||
					!relRoot;
			}

			if (this.querySize === undefined) {
				this.querySize = this.querySizeFixed ? 0 : defView.m_nPageSize;
			}

			if (this.bSupportsQuerySettings == null) {
				this.bSupportsQuerySettings = !!relRoot?.getTargetClass();
			}
		},

		/**
		 * Load Default Sort Settings if it doesn't already have one
		 * @returns {ICustomViewSort[]}
		 */
		loadDefaultSortSettings() {
			const {
				bSupportsQuerySettings,
				dataStoreInfo,
				defView,
				relRoot,
				sortSettings,
				specBuilder
			} = this;

			if (bSupportsQuerySettings && !sortSettings) {
				// initialize sort settings from those specified in view or data store
				const {defaultSourcePath, getKeyForHeading} = specBuilder;
				const sortObjects = relRoot.m_sorts || dataStoreInfo?.sortAttributes || [];
				const sorts = sortObjects.map((s) => {
					const {descending, ...sortObj} = s;
					const extendedPath = defaultSourcePath.plus(sortObj.path);
					const key = new Key({extendedPath, ...sortObj}).typedString;
					return {key, descending};
				});

				if (!sorts.length && !relRoot.bOrdered && !relRoot.typeSortAttribute) {
					// look for the key with Node Name as the id or prop and has no path
					const heading = defView.m_headings?.find((h) => h.isNameWithoutPath);
					const key = heading ? getKeyForHeading(heading) : new Key({extendedPath: defaultSourcePath, id: xml.ATTR_NAME});
					sorts.push({key: key.typedString, descending: false});
				}

				return sorts;
			}

			return sortSettings;
		},

		_setFilterSettingsAttr: function(/*Object*/obj) {
			// summary: need to reset cached stores when fixed query settings change
			var qr = this.getQueryResult(null, true);
			if (qr) {
				qr.store = null;
			}

			this._set("filterSettings", obj);
		},

		_setMainQueryLevelTotal: function(/*Number*/total) {
			// summary: set various query counts for number of results in view
			if (this.queryTotalCount == null) {
				this._set("queryDisplayCount", 0);
			}

			if (this.incrementalQuerySize && !this.querySize) {
				this._set("queryDisplayCountTarget", total);
			}

			this._set("queryTotalCount", total);
		},

		/**
		 * When query is set, also set queryForServer; the only time merged values should come into play is when setting
		 * the query value through the setQueryValue interface
		 * @param {Object} obj
		 */
		_setQueryAttr: function(obj) {
			this._set("queryForServer", obj);
			this._set("query", obj);
		},

		_setQuerySizeAttr: function(/*Number*/value) {
			// summary: allow changes to querySize only if it is not fixed for this view
			if (!this.querySizeFixed) {
				this._set("querySize", value);
			}
		},

		_setQuerySpecStoreAttr: function(/*Store*/store) {
			// summary: reset cached Values results when store of available specs change
			this._queryValueStores = {};

			// validate that ad-hoc query only contains keys that also exist in the new store
			var keysToRemove = _.filter(_.keys(this.query), function(key) {
				return !store || !store.get(key);
			});

			if (keysToRemove.length) {
				var q = _.omit(this.query, keysToRemove);
				var qs = q;

				if (this.queryForServer != this.query) {
					qs = _.omit(this.queryForServer, keysToRemove);
				}

				this._set("queryForServer", qs);
				this._set("query", q);
			}

			this._set("querySpecStore", store);
		},

		_setStoreAttr: function(/*Store*/store) {
			// summary: check for special case when setting store and using query levels
			var qr = this.getQueryResult(null, true);
			if (qr) {
				qr.store = store;
			}

			this._set("store", store);
		}
	});
});